/**
 * Kitchen calculations — pure function, no React.
 * Given kitchen state → returns { materialSummary, cutList, warnings, costs }.
 */
import {
  CARCASS_BY_ID, SHUTTER_BY_ID, DRAWER_BY_ID, TROLLEY_BY_ID,
  COUNTERTOP_BY_ID, EDGE_BY_ID, BACKSPLASH_BY_ID, APPLIANCE_BY_ID,
  SINK_BY_ID, FAUCET_BY_ID, ELECTRICAL_ITEMS,
  PLUMBING_BY_SCOPE, PLUMBING_ADDONS, PLUMBING_ADDON_BY_ID, PLUMBING_MATERIALS_FLAT,
  HARDWARE_DEFAULTS, hingesForHeight, getModuleDef,
} from './kitchenConfig';

export function calculateKitchen(kitchen, globalRates) {
  const { modules, countertop, backsplash, appliances, appliancesMode, sink, electrical } = kitchen;
  const mm2sqft = (wmm, hmm) => (wmm * hmm) / (304.8 * 304.8);

  // iter-305 (P1): manual include/exclude + rate override for Carcass / Shutter.
  const defaults = kitchen.defaults || {};
  const carcassIncluded = defaults.carcassIncluded !== false; // default true
  const shutterIncluded = defaults.shutterIncluded !== false;
  const carcassRateOverride = Number.isFinite(+defaults.carcassRateOverride) && +defaults.carcassRateOverride > 0
    ? +defaults.carcassRateOverride : null;
  const shutterRateOverride = Number.isFinite(+defaults.shutterRateOverride) && +defaults.shutterRateOverride > 0
    ? +defaults.shutterRateOverride : null;

  const materialSummary = [];
  const cutList = [];
  const warnings = [];
  let carcassCost = 0, shutterCost = 0, drawerCost = 0, trolleyCost = 0, hardwareCost = 0;
  let totalHinges = 0, totalLiftStays = 0, totalHandles = 0, totalDrawerSlides = 0;

  // Track carcass usage per material for summary
  const carcassAreaByMat = {};
  const shutterAreaByMat = {};

  modules.forEach((m, idx) => {
    const def = getModuleDef(m.category, m.type);
    if (!def) return;
    const qty = Math.max(1, m.qty);

    // Carcass surface area per unit: sides + top/bottom + back
    // Simple model: 2 sides (H × D) + top (W × D) + bottom (W × D) + back (W × H)
    const sideArea = mm2sqft(m.depthMm, m.heightMm) * 2;
    const topBottomArea = mm2sqft(m.widthMm, m.depthMm) * 2;
    const backArea = mm2sqft(m.widthMm, m.heightMm);
    const carcassAreaPerUnit = sideArea + topBottomArea + backArea;
    const totalCarcassArea = carcassAreaPerUnit * qty;
    const carcassMat = CARCASS_BY_ID[m.carcass] || CARCASS_BY_ID.bwr;
    const effCarcassRate = carcassIncluded ? (carcassRateOverride ?? carcassMat.rate) : 0;
    carcassCost += totalCarcassArea * effCarcassRate;
    if (carcassIncluded) carcassAreaByMat[carcassMat.label] = (carcassAreaByMat[carcassMat.label] || 0) + totalCarcassArea;

    // iter-304c: Handles count must reflect actual visible pull-handles.
    //  • Drawer-bearing module → 1 handle per drawer face (e.g. 3-drawer base = 3 handles)
    //  • Pure shutter module → 1 handle per 600mm of width (e.g. 900mm base = 2 handles)
    //  • Module qty multiplier applies after the per-module count is determined.
    const drawers0 = Math.max(0, m.drawerCount || 0);
    const handlesPerModule = drawers0 > 0
      ? drawers0
      : Math.max(1, Math.ceil((m.widthMm || 600) / 600));
    
    // Shutter (if applicable) — 'none' means omitted
    if (def.hasShutter && m.shutter && m.shutter !== 'none') {
      const shutterArea = mm2sqft(m.widthMm, m.heightMm) * qty;
      const shutterMat = SHUTTER_BY_ID[m.shutter] || SHUTTER_BY_ID.laminate;
      const effShutterRate = shutterIncluded ? (shutterRateOverride ?? shutterMat.rate) : 0;
      shutterCost += shutterArea * effShutterRate;
      if (shutterIncluded) shutterAreaByMat[shutterMat.label] = (shutterAreaByMat[shutterMat.label] || 0) + shutterArea;

      // Hardware auto-count
      const hinges = hingesForHeight(m.heightMm);
      totalHinges += hinges * qty;
      totalHandles += handlesPerModule * qty;
      if (def.liftUp) totalLiftStays += 2 * qty; // 2 stays per lift-up
    } else if (!def.hasShutter && m.shutter && m.shutter !== 'none') {
      // Override: module normally hasShutter=false but user chose to add a facia (e.g., chimney cover, drawer facia)
      const shutterArea = mm2sqft(m.widthMm, m.heightMm) * qty;
      const shutterMat = SHUTTER_BY_ID[m.shutter] || SHUTTER_BY_ID.laminate;
      const effShutterRate = shutterIncluded ? (shutterRateOverride ?? shutterMat.rate) : 0;
      shutterCost += shutterArea * effShutterRate;
      if (shutterIncluded) shutterAreaByMat[`${shutterMat.label} (Facia)`] = (shutterAreaByMat[`${shutterMat.label} (Facia)`] || 0) + shutterArea;
      totalHandles += handlesPerModule * qty;
    }

    // Drawers (channels/tandems) + trolley — use editable admin rates if set
    const drawers = Math.max(0, m.drawerCount);
    const drawerSysDef = DRAWER_BY_ID[m.drawerSystem] || DRAWER_BY_ID.none;
    const drawerRate = +(kitchen.drawerRates?.[m.drawerSystem] ?? drawerSysDef.rate);
    drawerCost += drawers * drawerRate * qty;
    totalDrawerSlides += drawers * qty;

    if (m.trolley && m.trolley !== 'none') {
      const trDef = TROLLEY_BY_ID[m.trolley];
      if (trDef) {
        const trRate = +(kitchen.trolleyRates?.[m.trolley] ?? trDef.rate);
        trolleyCost += trRate * qty;
      }
    }

    // Cut list entries (simplified: each module = 1 cabinet)
    cutList.push({
      code: `${m.category.charAt(0).toUpperCase()}${idx + 1}`,
      part: `${def.label} (${qty}×)`,
      qty,
      w: m.widthMm, h: m.heightMm,
      material: carcassMat.label,
      laminate: def.hasShutter ? SHUTTER_BY_ID[m.shutter]?.label : '—',
    });

    // ===== SMART WARNINGS =====
    if (m.widthMm > 900 && (m.category === 'base' || m.category === 'wall')) {
      warnings.push(`Module ${idx + 1} (${def.label}): width ${m.widthMm}mm exceeds 900mm — consider splitting for structural integrity.`);
    }
    if (m.category === 'base' && m.type === 'sink' && m.drawerCount > 0) {
      warnings.push(`Module ${idx + 1}: Sink unit with drawers can conflict with plumbing. Consider using shutters instead.`);
    }
    if (m.category === 'base' && m.type === 'drawer' && m.widthMm > 900) {
      warnings.push(`Module ${idx + 1}: Drawer width ${m.widthMm}mm > 900mm risks sag. Use tandem supports.`);
    }
  });

  // Hardware totals (hinges, handles, lift-stays, trolleys — all count as "hardware")
  // iter-304c (P4): allow per-estimate overrides from the Brand Portfolio panel
  // (mirrored from Storage estimator). When `hardware_overrides` is set on the
  // kitchen state, those rates take precedence over the HARDWARE_DEFAULTS.
  const hwo = kitchen.hardware_overrides || {};
  const handleRate = Number.isFinite(+hwo.handleRate) ? +hwo.handleRate : HARDWARE_DEFAULTS.handleRate;
  const hingeRate = Number.isFinite(+hwo.hingeRate) ? +hwo.hingeRate : HARDWARE_DEFAULTS.softCloseHingeRate;
  const liftStayRate = Number.isFinite(+hwo.liftStayRate) ? +hwo.liftStayRate : HARDWARE_DEFAULTS.liftUpStayRate;
  hardwareCost = (totalHinges * hingeRate) + (totalHandles * handleRate) + (totalLiftStays * liftStayRate) + trolleyCost;

  // ===== COUNTERTOP =====
  const isCustomCt = countertop.material === 'custom';
  const ctMat = isCustomCt
    ? { id: 'custom', label: (countertop.customName || 'Custom').trim() || 'Custom', rate: Math.max(0, +(countertop.customRate) || 0) }
    : (COUNTERTOP_BY_ID[countertop.material] || COUNTERTOP_BY_ID.granite);
  const ctDepthFt = (countertop.depthFt || 2);
  const ctArea = countertop.runningFt * ctDepthFt;
  const edge = EDGE_BY_ID[countertop.edge] || EDGE_BY_ID.plain;
  const edgeCost = countertop.runningFt * edge.rate;
  const cutoutsCost = (countertop.sinkCutout ? 1500 : 0) + (countertop.hobCutout ? 1200 : 0);
  const countertopCost = ctArea * ctMat.rate + edgeCost + cutoutsCost;

  // ===== BACKSPLASH =====
  const bsMat = BACKSPLASH_BY_ID[backsplash.material] || BACKSPLASH_BY_ID.none;
  const bsArea = backsplash.runningFt * backsplash.heightFt;
  const backsplashCost = bsArea * bsMat.rate;

  // ===== APPLIANCES =====
  const appliancesCost = appliancesMode === 'client' ? 0 :
    Object.values(appliances || {}).reduce((a, v) => a + (+v || 0), 0);

  // ===== SINK (hardware) =====
  const sinkMat = SINK_BY_ID[sink.type];
  const faucetMat = FAUCET_BY_ID[sink.faucet];
  const sinkCost = (sinkMat?.rate || 0) + (faucetMat?.rate || 0) + (sink.roCutout ? 400 : 0);

  // ===== PLUMBING ENGINE (scope-based + add-ons + optional materials) =====
  const plumbing = kitchen.plumbing || { scope: 'standard', billingMode: 'lump', addons: {}, includeMaterial: false, customRate: 0, customLabel: '' };
  const plumbingScopeDef = PLUMBING_BY_SCOPE[plumbing.scope] || PLUMBING_BY_SCOPE.standard;
  const plumbingScopeRate = plumbing.scope === 'custom'
    ? (+plumbing.customRate || 0)
    : (+(kitchen.plumbingRates?.[plumbing.scope] ?? plumbingScopeDef.baseRate));
  let plumbingAddonCost = 0;
  if (plumbing.billingMode === 'itemwise') {
    PLUMBING_ADDONS.forEach(a => {
      const qty = +(plumbing.addons?.[a.id] || 0);
      const rate = +(kitchen.plumbingAddonRates?.[a.id] ?? a.rate);
      plumbingAddonCost += qty * rate;
    });
  }
  const plumbingMaterialCost = plumbing.includeMaterial ? (kitchen.plumbingMaterialFlat != null ? +kitchen.plumbingMaterialFlat : PLUMBING_MATERIALS_FLAT) : 0;
  const plumbingCost = (plumbing.billingMode === 'itemwise' ? plumbingAddonCost : plumbingScopeRate) + plumbingMaterialCost;

  // ===== ELECTRICAL (uses admin-amendable rates if provided) =====
  let electricalCost = 0;
  ELECTRICAL_ITEMS.forEach(el => {
    const qty = +(electrical?.[el.id] || 0);
    const rate = +(kitchen.electricalRates?.[el.id] ?? el.rate);
    electricalCost += qty * rate;
  });

  // ===== LABOR, TRANSPORT, DESIGN, MARGIN =====
  const modulesCarcassArea = Object.values(carcassAreaByMat).reduce((a, b) => a + b, 0);
  const laborCost = modulesCarcassArea * (globalRates.laborCost || 450) * 0.5; // half of wardrobe labor per sqft (kitchens have more prefab)
  const transportCost = globalRates.transportCost || 1500;

  const subtotalHardCosts =
    carcassCost + shutterCost + drawerCost + trolleyCost + hardwareCost +
    countertopCost + backsplashCost + sinkCost + plumbingCost + electricalCost + laborCost + transportCost;

  // iter-306: respect 0% — `||` was treating 0 as falsy and defaulting to 5/10.
  const designPct = globalRates.designChargePercent != null ? +globalRates.designChargePercent : 5;
  const marginPct = globalRates.profitMargin != null ? +globalRates.profitMargin : 10;
  const designCharges = subtotalHardCosts * (designPct / 100);
  const margin = subtotalHardCosts * (marginPct / 100);
  const totalProjectCost = subtotalHardCosts + designCharges + margin + appliancesCost;

  // ===== MATERIAL SUMMARY =====
  // iter-304d (P2): tag every row with a `kind` so the Design-tab UI can split
  // into 4 distinct tables: material / hardware / labour / design. The full
  // `materialSummary` array stays in original order for backward compat.
  Object.entries(carcassAreaByMat).forEach(([name, area]) => {
    const mat = Object.values(CARCASS_BY_ID).find(c => c.label === name);
    const rate = carcassRateOverride ?? (mat?.rate || 75);
    materialSummary.push({ kind: 'material', name: `Carcass — ${name}${carcassRateOverride ? ' (rate override)' : ''}`, used: area, sheets: Math.ceil(area / 32), rate_per_sheet: rate * 32, unit_rate: rate, unit: 'sqft', total_cost: area * rate, wastage: ((Math.ceil(area / 32) * 32 - area) / (area || 1)) * 100 });
  });
  Object.entries(shutterAreaByMat).forEach(([name, area]) => {
    const mat = Object.values(SHUTTER_BY_ID).find(c => c.label === name);
    const rate = shutterRateOverride ?? (mat?.rate || 140);
    materialSummary.push({ kind: 'material', name: `Shutter — ${name}${shutterRateOverride ? ' (rate override)' : ''}`, used: area, sheets: Math.ceil(area / 32), rate_per_sheet: rate * 32, unit_rate: rate, unit: 'sqft', total_cost: area * rate, wastage: ((Math.ceil(area / 32) * 32 - area) / (area || 1)) * 100 });
  });
  if (totalHinges > 0) materialSummary.push({ kind: 'hardware', name: `Soft-Close Hinges`, used: totalHinges, sheets: totalHinges, rate_per_sheet: hingeRate, unit_rate: hingeRate, unit: 'Nos', total_cost: totalHinges * hingeRate, wastage: 0 });
  if (totalHandles > 0) materialSummary.push({ kind: 'hardware', name: `Handles / Profile Handles`, used: totalHandles, sheets: totalHandles, rate_per_sheet: handleRate, unit_rate: handleRate, unit: 'Nos', total_cost: totalHandles * handleRate, wastage: 0 });
  if (totalLiftStays > 0) materialSummary.push({ kind: 'hardware', name: `Lift-Up Stays (pairs)`, used: totalLiftStays, sheets: totalLiftStays, rate_per_sheet: liftStayRate, unit_rate: liftStayRate, unit: 'Pairs', total_cost: totalLiftStays * liftStayRate, wastage: 0 });
  if (totalDrawerSlides > 0) {
    const unitRate = totalDrawerSlides > 0 ? drawerCost / totalDrawerSlides : 0;
    materialSummary.push({ kind: 'hardware', name: `Drawer Slides / Tandems`, used: totalDrawerSlides, sheets: totalDrawerSlides, rate_per_sheet: unitRate, unit_rate: unitRate, unit: 'Sets', total_cost: drawerCost, wastage: 0 });
  }
  if (trolleyCost > 0) materialSummary.push({ kind: 'hardware', name: `Trolleys / Corner Systems`, used: 1, sheets: 1, rate_per_sheet: trolleyCost, unit_rate: trolleyCost, unit: 'Lot', total_cost: trolleyCost, wastage: 0 });
  if (countertopCost > 0) materialSummary.push({ kind: 'material', name: `Countertop — ${ctMat.label} (${ctArea.toFixed(1)} sqft)`, used: ctArea, sheets: Math.ceil(ctArea), rate_per_sheet: ctMat.rate, unit_rate: ctMat.rate, unit: 'sqft', total_cost: countertopCost, wastage: 8 });
  if (backsplashCost > 0) materialSummary.push({ kind: 'material', name: `Backsplash — ${bsMat.label}`, used: bsArea, sheets: Math.ceil(bsArea), rate_per_sheet: bsMat.rate, unit_rate: bsMat.rate, unit: 'sqft', total_cost: backsplashCost, wastage: 10 });
  if (sinkCost > 0) materialSummary.push({ kind: 'material', name: `Sink + Faucet`, used: 1, sheets: 1, rate_per_sheet: sinkCost, unit_rate: sinkCost, unit: 'Set', total_cost: sinkCost, wastage: 0 });
  if (plumbingCost > 0) materialSummary.push({ kind: 'labour', name: `Plumbing Work (${plumbingScopeDef.label}${plumbing.billingMode === 'itemwise' ? ' — item-wise' : ''}${plumbing.includeMaterial ? ' + material' : ''})`, used: 1, sheets: 1, rate_per_sheet: plumbingCost, unit_rate: plumbingCost, unit: 'Lot', total_cost: plumbingCost, wastage: 0 });
  if (electricalCost > 0) materialSummary.push({ kind: 'labour', name: `Electrical (sockets, LEDs, sensors)`, used: 1, sheets: 1, rate_per_sheet: electricalCost, unit_rate: electricalCost, unit: 'Lot', total_cost: electricalCost, wastage: 0 });
  if (appliancesCost > 0) materialSummary.push({ kind: 'material', name: `Appliances (${appliancesMode === 'allowance' ? 'allowance' : 'client-supplied'})`, used: Object.keys(appliances || {}).filter(k => appliances[k] > 0).length, sheets: 1, rate_per_sheet: appliancesCost, unit_rate: appliancesCost, unit: 'Lot', total_cost: appliancesCost, wastage: 0 });
  // Labour & Installation (carpenter, fitter, lift charges) and Transport
  if (laborCost > 0) materialSummary.push({ kind: 'labour', name: `Labour & Installation`, used: 1, sheets: 1, rate_per_sheet: laborCost, unit_rate: laborCost, unit: 'Lot', total_cost: laborCost, wastage: 0 });
  if (transportCost > 0) materialSummary.push({ kind: 'labour', name: `Transport / Lift Charges`, used: 1, sheets: 1, rate_per_sheet: transportCost, unit_rate: transportCost, unit: 'Lot', total_cost: transportCost, wastage: 0 });
  // Design fees — show the EFFECTIVE percent (was reading non-existent
  // kitchen.designChargePct, always rendering 0%).
  if (designCharges > 0) materialSummary.push({ kind: 'design', name: `Design Charges (${designPct}%)`, used: 1, sheets: 1, rate_per_sheet: designCharges, unit_rate: designCharges, unit: 'Lot', total_cost: designCharges, wastage: 0 });
  if (margin > 0) materialSummary.push({ kind: 'design', name: `Profit Margin (${marginPct}%)`, used: 1, sheets: 1, rate_per_sheet: margin, unit_rate: margin, unit: 'Lot', total_cost: margin, wastage: 0 });

  // Kitchen-specific layout warnings
  if (kitchen.layout === 'l_shape' || kitchen.layout === 'u_shape') {
    const hasCorner = modules.some(m => m.type === 'corner');
    if (!hasCorner) warnings.push(`${kitchen.layout.toUpperCase()} layout typically needs a Corner unit to utilize dead space — consider adding one.`);
  }
  const hobModule = modules.find(m => m.type === 'hob');
  const wallHinged = modules.filter(m => m.category === 'wall' && m.type === 'hinged');
  if (hobModule && wallHinged.length > 0) {
    warnings.push(`Ensure at least 650 mm clearance between hob top and any wall unit above it for safety.`);
  }
  if (countertop.runningFt > 0 && modules.filter(m => m.category === 'base').length === 0) {
    warnings.push(`Countertop specified but no base units added — typically one goes above the other.`);
  }

  return {
    materialSummary, cutList, warnings,
    carcassCost, shutterCost, drawerCost, trolleyCost, hardwareCost,
    countertopCost, backsplashCost, sinkCost, plumbingCost, electricalCost, appliancesCost,
    laborCost, transportCost, designCharges, margin,
    baseCost: subtotalHardCosts, totalProjectCost,
    totalHinges, totalHandles, totalDrawerSlides,
    // For compatibility with WardrobeCalculator UI
    area: modulesCarcassArea,
    totalEdgeBandingMeters: 0,
    adhesiveKg: 0,
    shutterCount: 0,
    totalLabor: laborCost,
    glassTotal: 0,
    productType: 'kitchen',
  };
}
